import { SolButton, SolIcon } from "@solstice/sol-react";
import { STATUS_ACTIVE } from "constant";
import { FC, useMemo, useState } from "react";
import { BrokerProps, ProfileProps } from "types";
import { Modal } from "ui-molecules";
import { PublishModalProfileSelection } from "./ProfileSelection";
import { PublishModalProcessing } from "./Processing";
import { PublishModalResults } from "./Results";

type PublishModalProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  broker: BrokerProps;
  onFinish?: (processedIds: number[]) => void;
  action?: "publish" | "unpublish";
};
export type ProcessingResults = {
  successfulCount: number;
  failedCount: number;
  errors: { id: number; message: string }[];
};

type PublishModalState = "profile-selection" | "publish" | "results";

const PublishModal: FC<PublishModalProps> = ({
  isOpen,
  setIsOpen,
  broker,
  onFinish,
  action = "publish",
}) => {
  const activeProfiles = useMemo(
    () =>
      broker.profiles?.filter(
        (profile) => profile.active_status === STATUS_ACTIVE
      ) ?? [],
    [broker.profiles]
  );
  const [state, setState] = useState<PublishModalState>("profile-selection");
  const [selectedProfiles, setSelectedProfiles] = useState<ProfileProps[]>([]);
  const [results, setResults] = useState<ProcessingResults>({
    successfulCount: 0,
    failedCount: 0,
    errors: [],
  });

  const handleClose = () => {
    setIsOpen(false);
    setState("profile-selection");
    setSelectedProfiles([]);
    setResults({
      successfulCount: 0,
      failedCount: 0,
      errors: [],
    });
  };

  const handleProfileSelection = (profiles: ProfileProps[]) => {
    setSelectedProfiles(profiles);
    setState("publish");
  };

  const handlePublish = (results: ProcessingResults) => {
    setResults(results);
    setState("results");
    if (onFinish) {
      const failedIds = results.errors.map((error) => error.id);
      const successfulIds = selectedProfiles
        .filter((profile) => !failedIds.includes(profile.id))
        .map((profile) => profile.id);
      onFinish(successfulIds);
    }
  };

  const modalProfileSelection = (
    <PublishModalProfileSelection
      profiles={activeProfiles}
      onSelectProfiles={handleProfileSelection}
      onCancel={handleClose}
    />
  );

  const modalProfilePublish = (
    <PublishModalProcessing
      profiles={selectedProfiles}
      onFinish={handlePublish}
      action={action}
    />
  );

  const modalResults = (
    <PublishModalResults
      results={results}
      profiles={selectedProfiles}
      onClose={handleClose}
      action={action}
    />
  );

  return (
    <Modal isOpen={isOpen} setIsOpen={handleClose} size="medium">
      <Modal.Header>
        <div className="w-full flex flex-row justify-between">
          <div className="flex flex-col">
            <h2 className="text-xl">{action === "publish" ? "Publish" : "Unpublish"}</h2>
          </div>
          <SolButton variant="tertiary" onSol-click={handleClose}>
            <SolIcon icon="close" />
          </SolButton>
        </div>
      </Modal.Header>
      {state === "profile-selection" && modalProfileSelection}
      {state === "publish" && modalProfilePublish}
      {state === "results" && modalResults}
    </Modal>
  );
};

export default PublishModal;
